import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Select, Alert, Space, Typography, Tag } from 'antd';
import { VideoCameraOutlined, SettingOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import WebSocketVideo from '../../components/WebSocketVideo';
import { api } from '../../utils/api';

const { Title, Text } = Typography;
const { Option } = Select;

const PageContainer = styled.div`
  padding: 0;
  background: #f0f2f5;
  min-height: 100vh;
`;

const StyledCard = styled(Card)`
  margin-bottom: 12px;
  border-radius: 8px;
  border: 1px solid #f0f0f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
`;

const VideoCard = styled(Card)`
  border-radius: 8px;
  border: 1px solid #f0f0f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;

  .ant-card-body {
    padding: 0;
  }
`;

const VideoWrapper = styled.div`
  position: relative;
  width: 100%;
  padding-top: 56.25%; /* 16:9 Aspect Ratio */
  background: #000;
  border-radius: 8px;
  overflow: hidden;

  & > div {
    position: absolute !important;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: transparent !important;
  }

  canvas {
    position: absolute !important;
    top: 0;
    left: 0;
    width: 100% !important;
    height: 100% !important;
    object-fit: contain;
    filter: none !important;
    opacity: 1 !important;
  }
`;

const CameraTitle = styled.div`
  position: absolute;
  top: 16px;
  left: 16px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 8px 16px;
  border-radius: 8px;
  font-weight: 500;
  z-index: 10;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ControlPanel = styled.div`
  background: white;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 12px;
`;

const LiveStream = () => {
  const [cameras, setCameras] = useState([]);
  const [selectedCameras, setSelectedCameras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [gridLayout, setGridLayout] = useState('1x1'); // 1x1, 2x2, 3x3, 4x4
  const fps = 20;  // Higher FPS for real-time streaming
  const detection = true;  // Enable face detection bounding boxes

  useEffect(() => {
    loadCameras();
  }, []);

  // Keyboard navigation for camera switching (A/D keys)
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Only handle if in single camera mode
      if (gridLayout !== '1x1' || cameras.length === 0) return;

      const currentIndex = cameras.findIndex(cam => cam.camera_id === selectedCameras[0]);

      if (e.key === 'a' || e.key === 'A') {
        // Previous camera
        const prevIndex = currentIndex > 0 ? currentIndex - 1 : cameras.length - 1;
        setSelectedCameras([cameras[prevIndex].camera_id]);
      } else if (e.key === 'd' || e.key === 'D') {
        // Next camera
        const nextIndex = currentIndex < cameras.length - 1 ? currentIndex + 1 : 0;
        setSelectedCameras([cameras[nextIndex].camera_id]);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [gridLayout, cameras, selectedCameras]);

  const loadCameras = async () => {
    try {
      setLoading(true);
      const response = await api.getCameras();
      setCameras(response.cameras || []);

      // Auto-select first camera
      if (response.cameras && response.cameras.length > 0) {
        setSelectedCameras([response.cameras[0].camera_id]);
      }

      // Check if main.py is running
      if (!response.main_py_running) {
        setError('No active camera feeds detected. Please ensure main.py is running to start the face recognition system.');
      } else {
        setError(null);
      }
    } catch (err) {
      console.error('Failed to load cameras:', err);
      setError('Failed to load camera list. Please check API connection.');
    } finally {
      setLoading(false);
    }
  };

  const getMaxCameras = () => {
    const layout = parseInt(gridLayout.split('x')[0]);
    return layout * layout;
  };

  const handleLayoutChange = (value) => {
    setGridLayout(value);
    const maxCameras = parseInt(value.split('x')[0]) * parseInt(value.split('x')[0]);
    if (selectedCameras.length > maxCameras) {
      setSelectedCameras(selectedCameras.slice(0, maxCameras));
    }
  };

  const handleCameraSelect = (values) => {
    const maxCameras = getMaxCameras();
    if (values.length <= maxCameras) {
      setSelectedCameras(values);
    } else {
      setSelectedCameras(values.slice(0, maxCameras));
    }
  };

  return (
    <PageContainer>
      {error && (
        <Alert
          message="Connection Error"
          description={
            <div>
              {error}
              <br /><br />
              <strong>Note:</strong> The live stream requires the main face recognition system to be running.
              <br />
              Please start <code>main.py</code> first, then refresh this page.
            </div>
          }
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 12 }}
        />
      )}

      <ControlPanel>
        <Row gutter={[16, 16]} align="middle">
          <Col span={5}>
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <Text strong>
                <SettingOutlined /> Grid Layout
              </Text>
              <Select
                value={gridLayout}
                onChange={handleLayoutChange}
                style={{ width: '100%' }}
              >
                <Option value="1x1">Single Camera (1×1)</Option>
                <Option value="2x2">Quad View (2×2)</Option>
                <Option value="3x3">Nine Cameras (3×3)</Option>
                <Option value="4x4">Sixteen Cameras (4×4)</Option>
              </Select>
            </Space>
          </Col>

          <Col span={13}>
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <Text strong>
                <VideoCameraOutlined /> Select Cameras (Max: {getMaxCameras()})
              </Text>
              <Select
                mode="multiple"
                value={selectedCameras}
                onChange={handleCameraSelect}
                style={{ width: '100%' }}
                placeholder="Select cameras to display"
                loading={loading}
                maxTagCount="responsive"
              >
                {cameras.map(camera => (
                  <Option key={camera.camera_id} value={camera.camera_id}>
                    Camera {camera.camera_id} {camera.zone ? `- ${camera.zone}` : ''}
                  </Option>
                ))}
              </Select>
            </Space>
          </Col>

          {gridLayout === '1x1' && cameras.length > 1 && (
            <Col span={6}>
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                <Text strong style={{ opacity: 0 }}>.</Text>
                <Space>
                  <Tag color="blue" style={{ fontSize: 13, padding: '4px 12px' }}>
                    <LeftOutlined /> Press A for Previous
                  </Tag>
                  <Tag color="blue" style={{ fontSize: 13, padding: '4px 12px' }}>
                    Press D for Next <RightOutlined />
                  </Tag>
                </Space>
              </Space>
            </Col>
          )}
        </Row>
      </ControlPanel>

      {selectedCameras.length === 0 ? (
        <Alert
          message="No Cameras Selected"
          description="Please select at least one camera to start live streaming."
          type="info"
          showIcon
          icon={<VideoCameraOutlined />}
        />
      ) : (
        <Row gutter={[16, 16]}>
          {selectedCameras.map(cameraId => (
            <Col
              key={cameraId}
              span={gridLayout === '1x1' ? 24 : gridLayout === '2x2' ? 12 : gridLayout === '3x3' ? 8 : 6}
            >
              <VideoCard>
                <VideoWrapper>
                  <CameraTitle>
                    <VideoCameraOutlined />
                    Camera {cameraId} {cameras.find(c => c.camera_id === cameraId)?.zone ? `- ${cameras.find(c => c.camera_id === cameraId).zone}` : ''}
                  </CameraTitle>
                  <WebSocketVideo
                    cameraId={cameraId}
                    detection={detection}
                    fps={fps}
                  />
                </VideoWrapper>
              </VideoCard>
            </Col>
          ))}
        </Row>
      )}
    </PageContainer>
  );
};

export default LiveStream;
